import {
  ArrowLeft,
  CircleHelp,
  Globe2,
  PanelRightOpen,
  MessageSquareText,
  Phone,
  Star,
} from 'lucide-react'
import type { RefObject } from 'react'
import type { ReactNode } from 'react'

import { Button } from '../../../ui/Button/Button'
import { Spinner } from '../../../ui/Spinner/Spinner'
import { StatePanel } from '../../../ui/StatePanel/StatePanel'
import type {
  Conversation,
  ConversationMessage,
  ThreadSummary,
} from '../conversations-model'
import type { ThreadResolution } from '../thread-resolution'
import type { InteractionOperationalState } from '../interaction-operational-contracts'
import type {
  ConversationOwnershipState,
  ConversationSmsRoute,
} from '../conversation-ownership-contracts'
import type { ConversationOwnershipRepository } from '../conversation-ownership-repository'
import styles from '../ConversationsPage.module.css'
import { ConversationEmptyState } from './ConversationEmptyState'
import { MessageList } from './MessageList'
import { ThreadPicker } from './ThreadPicker'
import { ConversationTakeover } from './ConversationTakeover'
import { ConversationStatusStrip } from './ConversationStatusStrip'
import type { ActionRequestsView } from '../use-action-requests'

export function ThreadPane({
  conversation,
  resolution,
  threadSelected,
  messageStatus,
  messages,
  messageHistoryKey,
  hasOlderMessages,
  loadingOlderMessages,
  onBack,
  onShowDetails,
  detailsTriggerRef,
  onChooseThread,
  onChangeThread,
  onLoadOlderMessages,
  activeTab,
  onTabChange,
  callsContent,
  operationalState,
  operationalStatus,
  operationalBusy,
  onToggleReviewed,
  onToggleStar,
  actionRequests,
  ownershipState,
  ownershipStatus,
  ownershipRepository,
  smsRoute,
  smsRouteStatus,
  tenantId,
  aggregateId,
  onOwnershipState,
  onRefreshOwnership,
  onRefreshSmsRoute,
}: {
  conversation: Conversation | null
  resolution: { status: 'idle' | 'loading' } | ThreadResolution
  threadSelected: boolean
  messageStatus: 'idle' | 'loading' | 'ready' | 'forbidden' | 'error'
  messages: ConversationMessage[]
  messageHistoryKey: string | null
  hasOlderMessages: boolean
  loadingOlderMessages: boolean
  onBack: () => void
  onShowDetails: () => void
  detailsTriggerRef: RefObject<HTMLButtonElement>
  onChooseThread: (thread: ThreadSummary) => void
  onChangeThread: () => void
  onLoadOlderMessages: () => void
  activeTab: 'sms' | 'calls'
  onTabChange: (tab: 'sms' | 'calls') => void
  callsContent: ReactNode
  operationalState: InteractionOperationalState | null
  operationalStatus: 'idle' | 'loading' | 'ready' | 'error'
  operationalBusy: boolean
  onToggleReviewed?: () => void
  onToggleStar?: () => void
  actionRequests?: ActionRequestsView
  ownershipState: ConversationOwnershipState | null
  ownershipStatus: 'idle' | 'loading' | 'ready' | 'error'
  ownershipRepository?: ConversationOwnershipRepository
  smsRoute: ConversationSmsRoute | null
  smsRouteStatus: 'idle' | 'loading' | 'ready' | 'unavailable'
  tenantId: string
  aggregateId: string | null
  onOwnershipState: (state: ConversationOwnershipState) => void
  onRefreshOwnership: () => Promise<void>
  onRefreshSmsRoute: () => Promise<void>
}) {
  if (!conversation) {
    return (
      <section className={styles.threadPane} aria-label="Conversation thread">
        <ConversationEmptyState
          title="Select a conversation"
          description="Choose a recent conversation to view its message history."
        />
      </section>
    )
  }

  return (
    <section className={styles.threadPane} aria-label="Conversation thread">
      <header className={styles.threadHeader}>
        <Button
          className={styles.mobileBack}
          variant="ghost"
          type="button"
          onClick={onBack}
          aria-label="Back"
        >
          <ArrowLeft size={17} aria-hidden="true" />
        </Button>
        <div className={styles.threadIdentity}>
          <span className={styles.headerAvatar} aria-hidden="true">
            {conversation.channel === 'SMS' ? (
              <MessageSquareText size={17} />
            ) : conversation.channel === 'Voice' ? (
              <Phone size={17} />
            ) : conversation.channel === 'Web Widget' ? (
              <Globe2 size={17} />
            ) : (
              <CircleHelp size={17} />
            )}
          </span>
          <div>
            <h2>Latest {conversation.channel} activity</h2>
            <p>
              <time dateTime={new Date(conversation.timestamp).toISOString()}>
                {activityTime(conversation.timestamp)}
              </time>
            </p>
          </div>
        </div>
        <div className={styles.threadActions}>
          {threadSelected && resolution.status === 'choice-required' ? (
            <Button variant="ghost" type="button" onClick={onChangeThread}>
              Change thread
            </Button>
          ) : null}
          {operationalState ? (
            <Button
              className={`${styles.iconAction} ${operationalState.starred ? styles.iconActionOn : ''}`}
              variant="secondary"
              type="button"
              disabled={operationalBusy || !onToggleStar}
              onClick={onToggleStar}
              aria-label={
                operationalState.starred
                  ? 'Unstar conversation'
                  : 'Star conversation'
              }
              title={operationalState.starred ? 'Starred' : 'Star'}
            >
              <Star
                size={16}
                fill={operationalState.starred ? 'currentColor' : 'none'}
                aria-hidden="true"
              />
            </Button>
          ) : null}
          <Button
            ref={detailsTriggerRef}
            className={`${styles.detailsButton} ${styles.iconAction}`}
            variant="secondary"
            type="button"
            onClick={onShowDetails}
            aria-label="Details"
            title="Patient details"
          >
            <PanelRightOpen size={16} aria-hidden="true" />
          </Button>
        </div>
      </header>
      {operationalState ? (
        <ConversationStatusStrip
          operationalState={operationalState}
          activityAtMs={conversation.timestamp}
          actions={actionRequests}
          reviewBusy={operationalBusy}
          onToggleReviewed={onToggleReviewed}
        />
      ) : null}
      <div
        className={styles.workspaceTabs}
        role="tablist"
        aria-label="Conversation channel"
      >
        <button
          id="conversation-sms-tab"
          type="button"
          role="tab"
          aria-selected={activeTab === 'sms'}
          aria-controls="conversation-sms-panel"
          tabIndex={activeTab === 'sms' ? 0 : -1}
          onClick={() => onTabChange('sms')}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight' || event.key === 'End') {
              event.preventDefault()
              onTabChange('calls')
              const nextTab = event.currentTarget.nextElementSibling
              if (nextTab instanceof HTMLElement) nextTab.focus()
            }
          }}
        >
          <MessageSquareText size={14} aria-hidden="true" /> SMS
        </button>
        <button
          id="conversation-calls-tab"
          type="button"
          role="tab"
          aria-selected={activeTab === 'calls'}
          aria-controls="conversation-calls-panel"
          tabIndex={activeTab === 'calls' ? 0 : -1}
          onClick={() => onTabChange('calls')}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft' || event.key === 'Home') {
              event.preventDefault()
              onTabChange('sms')
              const previousTab = event.currentTarget.previousElementSibling
              if (previousTab instanceof HTMLElement) previousTab.focus()
            }
          }}
        >
          <Phone size={14} aria-hidden="true" /> Calls
        </button>
      </div>
      <div
        id="conversation-sms-panel"
        role="tabpanel"
        aria-labelledby="conversation-sms-tab"
        hidden={activeTab !== 'sms'}
        className={styles.threadBody}
      >
        {operationalStatus === 'error' ? (
          <div className={styles.workflowUnavailable} role="status">
            Status indicators are temporarily unavailable and may be incomplete.
          </div>
        ) : null}
        {threadSelected ? (
          <MessageList
            status={messageStatus}
            messages={messages}
            historyKey={messageHistoryKey}
            hasOlder={hasOlderMessages}
            loadingOlder={loadingOlderMessages}
            onLoadOlder={onLoadOlderMessages}
          />
        ) : resolution.status === 'loading' ? (
          <Spinner label="Resolving message thread" />
        ) : resolution.status === 'choice-required' ? (
          <ThreadPicker
            candidates={resolution.candidates}
            onSelect={onChooseThread}
          />
        ) : resolution.status === 'empty' ? (
          <ConversationEmptyState
            title="No available thread"
            description="No valid recent thread metadata is available for this conversation."
          />
        ) : resolution.status === 'error' ? (
          <StatePanel
            kind={resolution.kind === 'forbidden' ? 'forbidden' : 'error'}
            title="Thread unavailable"
            description={
              resolution.kind === 'forbidden'
                ? 'You do not have permission to view this thread.'
                : 'The thread metadata could not be loaded.'
            }
          />
        ) : null}
        {aggregateId && ownershipState && ownershipRepository ? (
          <ConversationTakeover
            tenantId={tenantId}
            aggregateId={aggregateId}
            smsRoute={smsRoute}
            state={ownershipState}
            repository={ownershipRepository}
            onState={onOwnershipState}
            onRefresh={onRefreshOwnership}
            onRefreshRoute={onRefreshSmsRoute}
          />
        ) : aggregateId && ownershipRepository ? (
          <div className={styles.takeoverBar}>
            <p>
              {smsRouteStatus === 'unavailable'
                ? 'Staff SMS reply isn’t available for this conversation yet.'
                : ownershipStatus === 'error'
                  ? 'Conversation ownership couldn’t be loaded. Retrying…'
                  : 'Loading conversation ownership…'}
            </p>
          </div>
        ) : null}
      </div>
      <div
        id="conversation-calls-panel"
        role="tabpanel"
        aria-labelledby="conversation-calls-tab"
        hidden={activeTab !== 'calls'}
        className={styles.callsTabPanel}
      >
        {callsContent}
      </div>
    </section>
  )
}

function activityTime(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(timestamp)
}
